// Shared helper for de-duplicating a player's nationality flags.
//
// Players carry a birth country and a nationality. When both resolve to the
// SAME country (e.g. an England-born England international, or an
// Argentina/Argentina player) the UI used to render the identical flag twice,
// which looks cheap. This helper collapses that case to a single flag while
// preserving BOTH flags when the two countries genuinely differ.
//
// A "country" here is the small object the API returns for birth_country /
// nationality: { id, name, image_url, ... }. Strings are tolerated too.

// Build a stable, case-insensitive identity key for a country so that
// "England" / "england" / a shared ISO code / a shared flag image all collapse
// to the same value. Returns null when there is nothing usable to compare.
const countryKey = (country) => {
  if (!country) return null;

  if (typeof country === "string") {
    const trimmed = country.trim().toLowerCase();
    return trimmed || null;
  }

  if (typeof country !== "object") return null;

  // Prefer an explicit id when present (most reliable signal of identity).
  if (country.id !== undefined && country.id !== null && country.id !== "") {
    return `id:${String(country.id).trim().toLowerCase()}`;
  }

  // Then an ISO / country code, if the shape carries one.
  const code = country.code || country.iso || country.country_code;
  if (code) {
    const trimmed = String(code).trim().toLowerCase();
    if (trimmed) return `code:${trimmed}`;
  }

  // Then the flag image itself: two slots pointing at the same flag are the
  // same country for rendering purposes.
  if (country.image_url) {
    const trimmed = String(country.image_url).trim().toLowerCase();
    if (trimmed) return `img:${trimmed}`;
  }

  // Finally fall back to the display name.
  const name = country.name || country.country || country.country_name;
  if (name) {
    const trimmed = String(name).trim().toLowerCase();
    if (trimmed) return `name:${trimmed}`;
  }

  return null;
};

/**
 * Given a player's two nationality slots (birth country + nationality), return
 * a de-duplicated, order-preserving list of the countries that should render.
 *
 * - Same country in both slots -> a single entry.
 * - Different countries -> both entries, original order kept.
 * - Falsy / empty slots are dropped.
 *
 * @param {object|string|null} first  Usually birth_country.
 * @param {object|string|null} second Usually nationality.
 * @returns {Array<object|string>} The flags to render (length 0, 1 or 2).
 */
export const dedupeNationalities = (first, second) => {
  const result = [];
  const seen = new Set();

  for (const country of [first, second]) {
    if (!country) continue;
    const key = countryKey(country);
    // If a country has no comparable key, keep it (cannot prove a duplicate).
    if (key !== null && seen.has(key)) continue;
    if (key !== null) seen.add(key);
    result.push(country);
  }

  return result;
};

/**
 * The ordered, de-duplicated citizenships to render for one person.
 *
 * Product rule (Marin, 2026-08-11): a player shows ALL of his citizenships and
 * the country whose national team he plays for comes FIRST. Jude Bellingham
 * (born England, Irish passport, plays for England) has to read "England,
 * Ireland" — not "Ireland" alone, which is what the old passport-only
 * `nationality` field produced.
 *
 * The backend now ships that whole ordered list as `nationalities`
 * (dashboard/utils/nationality_resolver.py). This helper prefers it and falls
 * back to the historical `birth_country` + `nationality` pair for payloads that
 * predate the field, so no surface can end up flagless while the two contracts
 * coexist.
 *
 * @param {object|null} source A player/staff row, or an explicit
 *   `{ nationalities, birth_country, nationality }` slice of one.
 * @returns {Array<object|string>} Countries in display order (may be empty).
 */
export const resolveNationalities = (source) => {
  if (!source) return [];

  // Preferred: the backend's ordered list (national team first). Still run it
  // through the dedupe pass — a mixed payload could repeat a country.
  if (Array.isArray(source.nationalities) && source.nationalities.length) {
    const result = [];
    const seen = new Set();
    for (const country of source.nationalities) {
      if (!country) continue;
      const key = countryKey(country);
      if (key !== null && seen.has(key)) continue;
      if (key !== null) seen.add(key);
      result.push(country);
    }
    if (result.length) return result;
  }

  // Legacy pair. `nationality` leads: on payloads that already carry the new
  // resolution it IS the prioritised country, and on older ones it is the
  // passport — either way it outranks the place of birth.
  return dedupeNationalities(source.nationality, source.birth_country);
};

/**
 * Convenience boolean: true when the two slots resolve to the same country and
 * the second (duplicate) flag should be suppressed.
 */
export const isSameNationality = (first, second) => {
  if (!first || !second) return false;
  const a = countryKey(first);
  const b = countryKey(second);
  if (a === null || b === null) return false;
  return a === b;
};

export default dedupeNationalities;
